import {
	AccountInfo,
	AuthenticationResult,
	EventType
} from '@azure/msal-browser'
import { User } from '@services/models'
import config from '@utils/config'
import { msalInstance } from '@utils/authConfig'

export type AuthStatus =
	| 'idle'
	| 'resolving'
	| 'resolved'
	| 'profile_incomplete'
	| 'error'
	| 'unauthenticated'

export type AuthState = {
	status: AuthStatus
	account: AccountInfo | null
	user: User | null
}

let state: AuthState = {
	status: 'idle',
	account: null,
	user: null
}

const listeners = new Set<(nextState: AuthState) => void>()

const notify = () => {
	listeners.forEach((listener) => listener(state))
}

const setState = (partial: Partial<AuthState>) => {
	state = { ...state, ...partial }
	notify()
}

export const subscribeAuthState = (callback: (nextState: AuthState) => void) => {
	listeners.add(callback)
	callback(state)
	return () => listeners.delete(callback)
}

export const getAuthState = () => state

const isProfileIncompleteError = (error: any): boolean => {
	const status = error?.status ?? error?.response?.status
	const code = error?.code ?? error?.response?.data?.code

	return (
		code === 'PROFILE_INCOMPLETE' ||
		code === 'USER_NOT_FOUND' ||
		status === 404
	)
}

export const persistResolvedUser = (
	user: User,
	account: AccountInfo | null = state.account
): boolean => {
	const resolvedAccount = account ?? msalInstance.getActiveAccount()
	const currentAccount = state.account

	if (
		resolvedAccount &&
		currentAccount &&
		currentAccount.homeAccountId !== resolvedAccount.homeAccountId
	) {
		console.warn(
			'[authResolver] Ignoring stale resolved user for account',
			resolvedAccount.homeAccountId,
			'while current account is',
			currentAccount.homeAccountId
		)
		return false
	}

	const nextState: AuthState = {
		status: 'resolved',
		account: resolvedAccount,
		user
	}

	state = nextState
	localStorage.setItem(
		config.localStorage.authUserKey,
		JSON.stringify(User.serialize(user))
	)
	notify()
	return true
}

const resolveUserForAccount = async (account: AccountInfo) => {
	if (
		state.status === 'resolving' &&
		state.account?.homeAccountId === account.homeAccountId
	) {
		console.warn(
			'[authResolver] Skipping duplicate resolution for account',
			account.homeAccountId
		)
		return
	}

	setState({ status: 'resolving', account, user: null })

	try {
		localStorage.removeItem(config.localStorage.authUserKey)
		const user = await User.buildAsync(account)
		if (state.account && state.account.homeAccountId !== account.homeAccountId) {
			console.warn(
				'[authResolver] Ignoring stale async resolution for account',
				account.homeAccountId,
				'after newer account',
				state.account.homeAccountId
			)
			return
		}
		persistResolvedUser(user, account)
	} catch (error: any) {
		if (state.account && state.account.homeAccountId !== account.homeAccountId) {
			console.warn(
				'[authResolver] Ignoring stale async failure for account',
				account.homeAccountId,
				'after newer account',
				state.account.homeAccountId
			)
			return
		}

		if (isProfileIncompleteError(error)) {
			console.warn(
				'[authResolver] Account is valid but profile is incomplete; keeping authenticated state for completion flow.',
				account.homeAccountId
			)
			setState({
				status: 'profile_incomplete',
				account,
				user: null
			})
			return
		}

		console.error(
			'[authResolver] Failed to build user for account',
			account.homeAccountId,
			error
		)
		localStorage.removeItem(config.localStorage.authUserKey)
		setState({ status: 'error', account, user: null })
	}
}

msalInstance.addEventCallback((event) => {
	if (
		event.eventType === EventType.LOGIN_SUCCESS ||
		event.eventType === EventType.SSO_SILENT_SUCCESS
	) {
		const payload = event.payload as AuthenticationResult | null
		if (payload?.account) {
			msalInstance.setActiveAccount(payload.account)
			void resolveUserForAccount(payload.account)
		} else {
			console.warn(
				'[authResolver] Login event without account payload; leaving state unchanged.'
			)
		}
		return
	}

	if (event.eventType === EventType.LOGOUT_SUCCESS) {
		localStorage.removeItem(config.localStorage.authUserKey)
		setState({ status: 'unauthenticated', account: null, user: null })
		return
	}

	if (event.eventType === EventType.HANDLE_REDIRECT_END) {
		const active = msalInstance.getActiveAccount()
		if (active) {
			if (state.account?.homeAccountId !== active.homeAccountId) {
				void resolveUserForAccount(active)
			}
			return
		}

		console.warn(
			'[authResolver] No active account after redirect end; retaining current auth state.'
		)
		if (state.status === 'idle') {
			setState({ status: 'unauthenticated', account: null, user: null })
		}
	}
})

export const initAuthFromCache = async () => {
	if (state.status !== 'idle') {
		return
	}

	const active = msalInstance.getActiveAccount()
	if (active) {
		await resolveUserForAccount(active)
		return
	}

	console.warn('[authResolver] No active MSAL account found on cold start.')
	setState({ status: 'unauthenticated', account: null, user: null })
}
